import React, { useState, useEffect, useCallback, useRef } from 'react';
import './CrosswordGrid.css';
import HintSection from './HintSection';
import HintButton from './HintButton';
import HintCounter from './HintCounter';
import Timer from './Timer';
import ClearButton from './ClearButton';
import PausePlayButton from './PausePlayButton';
import CrosswordGridDisplay from './CrosswordGridDisplay';
import UndoButton from './UndoButton';
import LeaderboardButton from './LeaderboardButton';
import GiveUpButton from './GiveUpButton';
import AssistButton from './AssistButton';
import AssistStrip from './AssistStrip';

const CrosswordGrid = ({
  grid,
  setGrid,
  selectedCell,
  onCellSelect,
  placedWords,      // From the fetched snapshot, owned by App.js
  tiles,
  usedTileIds,
  onClear,
  isComplete,
  selectedDirection,
  setSelectedDirection,
  onCellDoubleClick,
  timerActive,
  timer,
  setTimer,
  isPaused,
  setIsPaused,
  formatTime,
  timerRef,
  isFlipped,
  setIsFlipped,
  currentHintIndex,    // Current hint index (lifted to App.js for keyboard support)
  onNextHint,          // Handler for next hint
  onPrevHint,          // Handler for previous hint
  onGiveUp,            // Reveals the solution and completes; owned by App.js
  onCheckGrid,         // Assist: flag wrongly placed tiles
  onRevealTile,        // Assist: reveal the selected cell
  onRevealAnswer,      // Assist: reveal the current clue's answer
  assistCosts,         // { check, tile, answer } in seconds, for the labels
  penaltyFlash,        // Seconds just added, for the floating indicator
  onUndo,              // Handler for undo
  wrongCells,          // Set of "r,c" keys flagged by a check
  canUndo              // Whether undo is available
}) => {
  const [blink, setBlink] = useState(false);

  // One mode for the controls row instead of two independent flip flags. The
  // old pair (controlsRowFlipped / giveUpFlipped) could both be set, each had
  // its own uncoordinated 4s timeout, and neither was cleared on unmount.
  // '' | 'assists' | 'confirm-clear' | 'confirm-giveup'
  const [controlsMode, setControlsMode] = useState('');
  const modeTimeoutRef = useRef(null);

  const setModeWithTimeout = useCallback((mode, ms = 4000) => {
    clearTimeout(modeTimeoutRef.current);
    setControlsMode(mode);
    if (mode) modeTimeoutRef.current = setTimeout(() => setControlsMode(''), ms);
  }, []);

  useEffect(() => () => clearTimeout(modeTimeoutRef.current), []);

  // The solution and the snapshot live in App.js now.

  // Modify timer effect to use props
  useEffect(() => {
    if (timerActive && !isComplete && !isPaused) {
      timerRef.current = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    if (!timerActive || isComplete || isPaused) {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isComplete, timerActive, isPaused, setTimer, timerRef]);

  // **New Effect: Handle Completion**
  useEffect(() => {
    // console.log('isComplete', isComplete);
    if (isComplete) {
      // Trigger blink effect
      setBlink(true);
      // console.log('Blink effect triggered');
      // Stop the timer
      clearInterval(timerRef.current);

      // Remove blink effect after 3 seconds
      const blinkTimeout = setTimeout(() => {
        setBlink(false);
      }, 3000);

      return () => clearTimeout(blinkTimeout);
    }
  }, [isComplete, timerRef]);


  const handleClearLocal = useCallback(() => {
    if (!grid) return;
    // console.log('handleClear called from button click');
    // console.log('Current controlsRowFlipped value:', controlsRowFlipped);

    if (controlsMode !== 'confirm-clear') {
      setModeWithTimeout('confirm-clear');
      return;
    }

    // Note: this used to iterate cellTileIds and call onTileClick to return
    // tiles to the bank. cellTileIds was never populated and onTileClick was
    // never passed to this component, so it was a no-op that would have thrown
    // had either been real. onClear rehydrates from the snapshot instead.
    const newGrid = grid.map(row =>
      row.map(cell => ({
        ...cell,
        letter: cell.isLocked ? cell.letter : '',
        tileId: null, // Reset tileId to remove 'occupied' class
        cellDisplay: cell.isLocked ? 'locked' : (cell.isBlocked ? 'blocked' : 'open') // Reset cellDisplay
      }))
    );

    // Call onClear to reset the letter bank and other states in App.js
    if (onClear) {
      onClear();
    }

    setGrid(newGrid);
    onCellSelect(null);
    setModeWithTimeout('');
  }, [
    grid,
    onClear,
    setGrid,
    onCellSelect,
    controlsMode,
    setModeWithTimeout
  ]);

  const findWordAtCell = useCallback((rowIndex, colIndex, direction = null) => {
    // console.log('\n=== findWordAtCell ===');
    // console.log(`Checking cell [${rowIndex},${colIndex}]${direction ? ` direction ${direction}` : ''}`);
    // console.log('placedWords:', placedWords);

    if (!placedWords || placedWords.length === 0) {
      // console.log('No placed words available!');
      return null;
    }

    const matchingWords = placedWords.filter(word => {
      // console.log('\nChecking word:', {
      //   word: word.word,
      //   direction: word.direction,
      //   startRow: word.startRow,
      //   startCol: word.startCol
      // });

      // Calculate word boundaries
      const wordEndCol = word.startCol + word.word.length - 1;
      const wordEndRow = word.startRow + word.word.length - 1;

      // If direction is specified, only check that direction
      if (direction && word.direction !== direction) {
        return false;
      }

      if (word.direction === 'across') {
        const isInWord = rowIndex === word.startRow &&
          colIndex >= word.startCol &&
          colIndex <= wordEndCol;

        return isInWord;
      } else { // down
        const isInWord = colIndex === word.startCol &&
          rowIndex >= word.startRow &&
          rowIndex <= wordEndRow;

        return isInWord;
      }
    });

    // console.log('\nMatching words found:', matchingWords.length);
    // matchingWords.forEach(word => 
    //   console.log(`- "${word.word}" (${word.direction})`));

    if (matchingWords.length === 0) {
      // console.log('No words found at this cell');
      return null;
    }

    // If direction is specified, return the matching word in that direction
    if (direction) {
      const directedWord = matchingWords.find(w => w.direction === direction);
      // console.log(`Returning ${direction} word:`, directedWord?.word || 'none');
      return directedWord || null;
    }

    // If no direction specified, return the first word found
    // console.log('Returning first found word:', matchingWords[0].word);
    return matchingWords[0];
  }, [placedWords]);

  // Helper function to check if there are open cells in a given direction
  const hasOpenCellsInDirection = useCallback((row, col, direction) => {
    if (!grid) return false;

    if (direction === 'across') {
      // Check left
      if (col > 0) {
        const leftCell = grid[row][col - 1];
        if (!leftCell.isBlocked && !leftCell.letter && !leftCell.isLocked) return true;
      }
      // Check right
      if (col < grid[0].length - 1) {
        const rightCell = grid[row][col + 1];
        if (!rightCell.isBlocked && !rightCell.letter && !rightCell.isLocked) return true;
      }
    } else {
      // Check above
      if (row > 0) {
        const aboveCell = grid[row - 1][col];
        if (!aboveCell.isBlocked && !aboveCell.letter && !aboveCell.isLocked) return true;
      }
      // Check below
      if (row < grid.length - 1) {
        const belowCell = grid[row + 1][col];
        if (!belowCell.isBlocked && !belowCell.letter && !belowCell.isLocked) return true;
      }
    }

    return false;
  }, [grid]);

  // Helper function to update direction based on cell and current direction
  // Only switches if current direction has no open cells
  const updateDirectionForCell = useCallback((row, col) => {
    if (!grid) return;

    // Check for words in both directions
    const acrossWord = findWordAtCell(row, col, 'across');
    const downWord = findWordAtCell(row, col, 'down');
    
    // Check for open cells in each direction
    const hasAcrossOpen = hasOpenCellsInDirection(row, col, 'across');
    const hasDownOpen = hasOpenCellsInDirection(row, col, 'down');
    
    // Check if current direction has open cells
    const currentDirectionHasOpen = selectedDirection === 'across' ? hasAcrossOpen : hasDownOpen;

    // Only switch direction if current direction has NO open cells
    if (!currentDirectionHasOpen) {
      // If current direction is horizontal and has no open cells, switch to vertical if available
      if (selectedDirection === 'across') {
        if (downWord) {
          setSelectedDirection('down');
        }
      } 
      // If current direction is vertical and has no open cells, switch to horizontal if available
      else if (selectedDirection === 'down') {
        if (acrossWord) {
          setSelectedDirection('across');
        }
      }
    }
    // If current direction has open cells, don't change direction (even at intersections)
  }, [grid, selectedDirection, findWordAtCell, setSelectedDirection, hasOpenCellsInDirection]);

  // Track previous cell to avoid re-running on same cell
  const prevCellRef = useRef(null);
  
  // Update direction when selected cell changes (for keyboard navigation)
  // Only runs when the cell actually changes, not on every render
  useEffect(() => {
    if (selectedCell && grid) {
      const cellKey = `${selectedCell.row},${selectedCell.col}`;
      const prevCellKey = prevCellRef.current ? `${prevCellRef.current.row},${prevCellRef.current.col}` : null;
      
      // Only update if this is a different cell
      if (cellKey !== prevCellKey) {
        updateDirectionForCell(selectedCell.row, selectedCell.col);
        prevCellRef.current = selectedCell;
      }
    } else if (!selectedCell) {
      prevCellRef.current = null;
    }
  }, [selectedCell, grid, updateDirectionForCell]);

  const handleCellClick = (row, col) => {
    if (selectedCell?.row === row && selectedCell?.col === col) {

      // Cell is already selected, toggle direction if multiple directions are available
      // const currentWord = findWordAtCell(row, col, selectedDirection);
      // console.log('Current word:', currentWord);
      const otherDirection = selectedDirection === 'across' ? 'down' : 'across';
      const otherDirectionWord = findWordAtCell(row, col, otherDirection);

      // Only toggle if there's a valid word in the other direction AND there are open cells in that direction
      if (otherDirectionWord && hasOpenCellsInDirection(row, col, otherDirection)) {
        if (grid[row][col].letter === '' && !grid[row][col].isBlocked) {
          setSelectedDirection(otherDirection);
        }
      }
    } else {
      // New cell selection
      // console.log('grid[row][col]', grid[row][col]);
      if (grid[row][col].letter === '' && !grid[row][col].isBlocked) {
        onCellSelect({ row, col });
        // Direction will be updated by useEffect when selectedCell changes
      } else if (grid[row][col].isLocked) {
        const nextOpenCell = findNextOpenCell(row, col, selectedDirection, grid);
        // console.log('Blocked nextOpenCell', nextOpenCell);
        if (nextOpenCell) {
          onCellSelect(nextOpenCell);
          // Direction will be updated by useEffect when selectedCell changes
        }
      }
    }
  };

  /**
   * Helper function to find the next open cell in the specified direction.
   *
   * @param {number} startRow - Starting row index.
   * @param {number} startCol - Starting column index.
   * @param {string} direction - Direction to search ('across' or 'down').
   * @param {array} grid - Current grid state.
   * @returns {object|null} - Returns the next open cell as { row, col } or null if none found.
   */
  const findNextOpenCell = (startRow, startCol, direction, grid) => {
    let tempRow = startRow;
    let tempCol = startCol;

    while (tempRow < grid.length && tempCol < grid[0].length) {
      if (direction === 'across') {
        if (!grid[tempRow][tempCol].isBlocked && !grid[tempRow][tempCol].letter) {
          return { row: tempRow, col: tempCol };
        }
        tempCol += 1;
      } else if (direction === 'down') {
        if (!grid[tempRow][tempCol].isBlocked && !grid[tempRow][tempCol].letter) {
          return { row: tempRow, col: tempCol };
        }
        tempRow += 1;
      }
    }

    return null; // No open cell found
  }

  const isCorrectLetter = useCallback((rowIndex, colIndex, word, startRow, startCol, direction) => {
    if (!grid[rowIndex][colIndex].letter) {
      // console.log(`Cell [${rowIndex},${colIndex}] is empty`);
      return false;
    }

    let letterIndex;
    if (direction === 'across') {
      letterIndex = colIndex - startCol;
    } else {
      letterIndex = rowIndex - startRow;
    }

    const currentLetter = grid[rowIndex][colIndex].letter;
    const expectedLetter = word[letterIndex];

    // console.log(`Checking letter at [${rowIndex},${colIndex}]:`, {
    //   current: currentLetter,
    //   expected: expectedLetter,
    //   isCorrect: currentLetter === expectedLetter
    // });

    return currentLetter === expectedLetter;
  }, [grid]);

  const isPartOfSelectedWord = useCallback((rowIndex, colIndex) => {
    if (!selectedCell) {
      return false;
    }

    // console.log(`\nChecking if [${rowIndex},${colIndex}] is part of word from selected cell [${selectedCell.row},${selectedCell.col}] (${selectedDirection})`);

    // Check if cell is part of current word
    const currentWord = findWordAtCell(selectedCell.row, selectedCell.col, selectedDirection);
    if (!currentWord) {
      return false;
    }

    // console.log('Current word:', {
    //   word: currentWord.word,
    //   startRow: currentWord.startRow,
    //   startCol: currentWord.startCol,
    //   direction: currentWord.direction
    // });

    const isInWord = selectedDirection === 'across'
      ? (rowIndex === currentWord.startRow &&
        colIndex >= currentWord.startCol &&
        colIndex < currentWord.startCol + currentWord.word.length)
      : (colIndex === currentWord.startCol &&
        rowIndex >= currentWord.startRow &&
        rowIndex < currentWord.startRow + currentWord.word.length);

    // console.log(`Cell [${rowIndex},${colIndex}] is${isInWord ? '' : ' not'} part of the word`);

    if (!isInWord) return false;

    // Check if the letter is correct
    const isCorrect = isCorrectLetter(
      rowIndex,
      colIndex,
      currentWord.word,
      currentWord.startRow,
      currentWord.startCol,
      currentWord.direction
    );

    // console.log(`Cell [${rowIndex},${colIndex}] has${isCorrect ? '' : ' in'}correct letter`);

    return { isInWord: true, isCorrect };
  }, [selectedCell, selectedDirection, findWordAtCell, isCorrectLetter]);

  // getMultiTileLetters removed: placedMultiTiles was never populated, so it
  // always returned null and the branch that used it was dead.

  // **Effect: Check for Puzzle Completion and Log Results**
  // The completion scan now lives in App.js, which owns the solution.

  // **New Handler: Toggle Pause/Play**
  const handleTogglePause = () => {
    setIsPaused(prev => !prev);
    setIsFlipped(true);
  };


  // Handler to toggle Flip and Pause for leaderboard
  const handleLeaderboardClick = () => {
    setIsFlipped(true);
    setIsPaused(true);
  };

  // Handler for Give Up button.
  //
  // The reveal itself moved to App.js, which owns the solution now. This keeps
  // only the two-tap confirmation, which is this codebase's convention for
  // destructive actions.
  const handleGiveUp = useCallback(() => {
    if (!grid || isComplete) return;

    if (controlsMode !== 'confirm-giveup') {
      setModeWithTimeout('confirm-giveup');
      return;
    }

    setModeWithTimeout('');
    onGiveUp();
  }, [grid, isComplete, controlsMode, setModeWithTimeout, onGiveUp]);

  if (!grid) {
    return <div>Loading...</div>;
  }

  return (
    <div className={`crossword-container ${blink ? 'completed' : ''}`}>
      <HintSection
        placedWords={placedWords}
        currentHintIndex={currentHintIndex}
        handlePrevHint={onPrevHint}
        handleNextHint={onNextHint}
        timer={timer}
        formatTime={formatTime}
        handleClearLocal={handleClearLocal}
      />
      <div className="controls-wrapper">
        {controlsMode === 'assists' ? (
          <AssistStrip
            costs={assistCosts}
            canRevealTile={Boolean(selectedCell)}
            onCheck={() => { setModeWithTimeout(''); onCheckGrid(); }}
            onRevealTile={() => { setModeWithTimeout(''); onRevealTile(); }}
            onRevealAnswer={() => { setModeWithTimeout(''); onRevealAnswer(); }}
            onGiveUp={handleGiveUp}
            onClose={() => setModeWithTimeout('')}
          />
        ) : controlsMode === 'confirm-clear' ? (
          <div className="controls-row-back">
            Confirm puzzle reset?
            <ClearButton onClear={handleClearLocal} controlsRowFlipped="flipped" />
          </div>
        ) : controlsMode === 'confirm-giveup' ? (
          <div className="controls-row-back">
            Reveal the whole puzzle?
            <GiveUpButton onGiveUp={handleGiveUp} isComplete={isComplete} />
          </div>
        ) : (
          <div className="controls-row-front">
            <Timer timer={timer} formatTime={formatTime} penaltyFlash={penaltyFlash} />
            <PausePlayButton isPaused={isPaused} onToggle={handleTogglePause} />
            <HintCounter currentIndex={currentHintIndex + 1} total={placedWords.length} />
            <ClearButton onClear={handleClearLocal} controlsRowFlipped="" />
            {/* Replaces the give-up button rather than adding a slot — the row
                was already six items wide on mobile. Give-up now lives inside
                the strip, as the bottom rung of the same ladder. */}
            <AssistButton
              onClick={() => setModeWithTimeout('assists', 8000)}
              isComplete={isComplete}
            />
            {isComplete ? (
              <LeaderboardButton onLeaderboard={handleLeaderboardClick} />
            ) : (
              <UndoButton onUndo={onUndo} canUndo={canUndo} />
            )}
          </div>
        )}
      </div>
      <div className="puzzle-controls-row">
        <div>
          <HintButton onClick={onPrevHint} arrowText="←" ariaLabel="Previous hint" />
        </div>
        <CrosswordGridDisplay
          grid={grid}
          handleCellDoubleClick={onCellDoubleClick}
          handleCellClick={handleCellClick}
          isPartOfSelectedWord={isPartOfSelectedWord}
          isComplete={isComplete}
          selectedCell={selectedCell}
          wrongCells={wrongCells}
        />
        <div>
          <HintButton onClick={onNextHint} arrowText="→" ariaLabel="Next hint" />
        </div>
      </div>
    </div>
  );
};

export default CrosswordGrid;